import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
/**
 * Catálogo de la «Primera travesía»: los cofres (categorías) que se
 * proponen en los pasos 4 y 5, las metas del paso 6 y los cofres básicos de
 * «Saltar». Copia de MENSUALES, OCASIONALES, METAS y BASICAS de la maqueta
 * aprobada (`tutorial/primera-travesia.html`).
 *
 * Todo son claves: los nombres se traducen con `t()` (claves en inglés) en
 * `nombreCofre`, `nombreGrupo` y `descripcionMeta`. Los importes van en
 * céntimos, como en el resto de Actual.
 */
import type { TFunction } from 'i18next';

/** «¿Con quién vives?» (paso 2). */
export type Vive = 'familia' | 'sin' | 'pareja' | 'piso';
export const OPCIONES_VIVE: readonly Vive[] = [
  'familia',
  'sin',
  'pareja',
  'piso',
];

/** Respuestas del paso 2 que deciden la preselección de los pasos 4 y 5. */
export type Perfil = { vive: Vive; mascota: boolean; hijos: boolean };

export const PERFIL_POR_DEFECTO: Perfil = {
  vive: 'sin',
  mascota: false,
  hijos: false,
};

export type GrupoTravesia = 'casa' | 'dia' | 'vez' | 'obj';
export const GRUPOS: readonly GrupoTravesia[] = ['casa', 'dia', 'vez', 'obj'];

export type IdMensual =
  | 'vivienda'
  | 'suministros'
  | 'internet'
  | 'piso'
  | 'super'
  | 'transporte'
  | 'restaurantes'
  | 'ocio'
  | 'suscripciones'
  | 'videojuegos'
  | 'cuidado'
  | 'mascota'
  | 'hijos';

export type IdOcasional =
  | 'regalos'
  | 'ropa'
  | 'vacaciones'
  | 'medico'
  | 'coche'
  | 'anuales';

export type IdCofre = IdMensual | IdOcasional;

export type IdMeta = 'colchon' | 'viaje' | 'compra' | 'carne' | 'otra';

/** Cofre que se llena cada mes (paso 4): grupo Casa o Día a día. */
export type CofreMensual = {
  id: IdMensual;
  grupo: 'casa' | 'dia';
  emoji: string;
  /** Hueco de color 0..11 (`--p-k0..k11`, pref `category-colors`). */
  k: number;
  /** Lo sugerido cada mes, en céntimos. */
  sugerido: IntegerAmount;
  /** Viene marcado según el perfil. */
  si: (perfil: Perfil) => boolean;
  /** Solo se enseña con este perfil (mascota, hijos). */
  solo?: (perfil: Perfil) => boolean;
};

/** Cofre «De vez en cuando» (paso 5): objetivo anual. */
export type CofreOcasional = {
  id: IdOcasional;
  grupo: 'vez';
  emoji: string;
  k: number;
  /** Lo que se gasta al año, en céntimos. */
  anual: IntegerAmount;
  si: (perfil: Perfil) => boolean;
};

export type Meta = {
  id: IdMeta;
  emoji: string;
  k: number;
  /** Importe propuesto, en céntimos. */
  importe: IntegerAmount;
  /** Plazo propuesto, en meses. */
  meses: number;
};

const siempre = () => true;
const nunca = () => false;

export const MENSUALES: readonly CofreMensual[] = [
  {
    id: 'vivienda',
    grupo: 'casa',
    emoji: '🏠',
    k: 0,
    sugerido: 65000,
    si: p => p.vive !== 'familia',
  },
  {
    id: 'suministros',
    grupo: 'casa',
    emoji: '💡',
    k: 5,
    sugerido: 9000,
    si: p => p.vive !== 'familia',
  },
  {
    id: 'internet',
    grupo: 'casa',
    emoji: '📶',
    k: 1,
    sugerido: 3500,
    si: siempre,
  },
  {
    id: 'piso',
    grupo: 'casa',
    emoji: '🧻',
    k: 4,
    sugerido: 2500,
    si: p => p.vive === 'piso',
  },
  {
    id: 'super',
    grupo: 'dia',
    emoji: '🛒',
    k: 2,
    sugerido: 25000,
    si: p => p.vive !== 'familia',
  },
  {
    id: 'transporte',
    grupo: 'dia',
    emoji: '🚇',
    k: 3,
    sugerido: 4000,
    si: siempre,
  },
  {
    id: 'restaurantes',
    grupo: 'dia',
    emoji: '🍽️',
    k: 6,
    sugerido: 5000,
    si: siempre,
  },
  { id: 'ocio', grupo: 'dia', emoji: '🎬', k: 9, sugerido: 5000, si: siempre },
  {
    id: 'suscripciones',
    grupo: 'dia',
    emoji: '📺',
    k: 8,
    sugerido: 1500,
    si: siempre,
  },
  {
    id: 'videojuegos',
    grupo: 'dia',
    emoji: '🎮',
    k: 10,
    sugerido: 2000,
    si: p => p.vive === 'familia',
  },
  {
    id: 'cuidado',
    grupo: 'dia',
    emoji: '🧴',
    k: 11,
    sugerido: 2500,
    si: nunca,
  },
  {
    id: 'mascota',
    grupo: 'dia',
    emoji: '🐾',
    k: 7,
    sugerido: 4000,
    si: p => p.mascota,
    solo: p => p.mascota,
  },
  {
    id: 'hijos',
    grupo: 'dia',
    emoji: '🧸',
    k: 6,
    sugerido: 15000,
    si: p => p.hijos,
    solo: p => p.hijos,
  },
];

export const OCASIONALES: readonly CofreOcasional[] = [
  { id: 'regalos', grupo: 'vez', emoji: '🎁', k: 3, anual: 36000, si: siempre },
  { id: 'ropa', grupo: 'vez', emoji: '👕', k: 0, anual: 36000, si: siempre },
  {
    id: 'vacaciones',
    grupo: 'vez',
    emoji: '🏖️',
    k: 2,
    anual: 90000,
    si: p => p.vive !== 'familia',
  },
  { id: 'medico', grupo: 'vez', emoji: '🦷', k: 4, anual: 24000, si: nunca },
  { id: 'coche', grupo: 'vez', emoji: '🚗', k: 5, anual: 72000, si: nunca },
  { id: 'anuales', grupo: 'vez', emoji: '📅', k: 11, anual: 24000, si: nunca },
];

export const METAS: readonly Meta[] = [
  { id: 'colchon', emoji: '🛟', k: 7, importe: 100000, meses: 12 },
  { id: 'viaje', emoji: '✈️', k: 0, importe: 60000, meses: 9 },
  { id: 'compra', emoji: '💻', k: 8, importe: 40000, meses: 6 },
  { id: 'carne', emoji: '🚦', k: 5, importe: 80000, meses: 12 },
  { id: 'otra', emoji: '⭐', k: 10, importe: 30000, meses: 6 },
];

/** Cofres de «Saltar» (más la meta fondo de emergencia: 7 en total). */
export const BASICAS: readonly IdCofre[] = [
  'vivienda',
  'suministros',
  'super',
  'transporte',
  'ocio',
  'regalos',
];

/** Meta que crea «Saltar»: fondo de emergencia, 1.000 € en 12 meses. */
export const META_SALTAR: MetaElegida = {
  id: 'colchon',
  tipo: 'una',
  importe: 100000,
  meses: 12,
};

/** Chips de «Necesito» y «Plazo» del editor de la meta (paso 6), en céntimos. */
export const IMPORTES_META_UNA: readonly IntegerAmount[] = [
  30000, 50000, 100000, 200000,
];
export const IMPORTES_META_MES: readonly IntegerAmount[] = [
  2500, 5000, 10000, 20000,
];
export const PLAZOS_META: readonly number[] = [6, 12, 24];

/** Meta tal como queda tras el paso 6. */
export type MetaElegida = {
  id: IdMeta;
  /** «Una vez» (importe en un plazo) o «Cada mes» (importe al mes). */
  tipo: 'una' | 'mes';
  importe: IntegerAmount;
  /** Plazo en meses (solo cuenta con `tipo: 'una'`). */
  meses: number;
  /** Nombre propio de «Otra meta». */
  nombre?: string;
};

/** Meta del catálogo con sus valores propuestos (al tocar su tarjeta). */
export function metaInicial(id: IdMeta): MetaElegida {
  const m = METAS.find(x => x.id === id) ?? METAS[0];
  return { id: m.id, tipo: 'una', importe: m.importe, meses: m.meses };
}

export function cofreMensual(id: string): CofreMensual | undefined {
  return MENSUALES.find(c => c.id === id);
}

export function cofreOcasional(id: string): CofreOcasional | undefined {
  return OCASIONALES.find(c => c.id === id);
}

export function datosMeta(id: IdMeta): Meta {
  return METAS.find(m => m.id === id) ?? METAS[0];
}

/** Cofre del catálogo (mensual u ocasional) con lo común a los dos. */
export function cofre(
  id: string,
):
  | { id: IdCofre; grupo: 'casa' | 'dia' | 'vez'; emoji: string; k: number }
  | undefined {
  return cofreMensual(id) ?? cofreOcasional(id);
}

/** Mensuales que se enseñan en el paso 4 (mascota e hijos solo si toca). */
export function mensualesVisibles(perfil: Perfil): CofreMensual[] {
  return MENSUALES.filter(c => !c.solo || c.solo(perfil));
}

/** Preselección de los pasos 4 y 5 según el paso 2, en orden de catálogo. */
export function seleccionInicial(perfil: Perfil): {
  mensuales: IdMensual[];
  ocasionales: IdOcasional[];
} {
  return {
    mensuales: mensualesVisibles(perfil)
      .filter(c => c.si(perfil))
      .map(c => c.id),
    ocasionales: OCASIONALES.filter(c => c.si(perfil)).map(c => c.id),
  };
}

/** Lo que pide al mes un cofre ocasional («unos 30 €/mes»), en céntimos. */
export function cuotaOcasional(anual: IntegerAmount): IntegerAmount {
  return Math.round(anual / 12);
}

/** Lo sugerido al mes para cualquier cofre del catálogo, en céntimos. */
export function sugeridoCofre(id: string): IntegerAmount {
  const m = cofreMensual(id);
  if (m) {
    return m.sugerido;
  }
  const o = cofreOcasional(id);
  return o ? cuotaOcasional(o.anual) : 0;
}

/**
 * Cuota mensual de la meta: el importe si es «Cada mes»; si es «Una vez»,
 * el importe entre los meses redondeado al euro hacia arriba (1.000 € en
 * 12 meses → 84 €), como la línea «unos 84 €/mes» de la maqueta.
 */
export function cuotaMeta(meta: MetaElegida): IntegerAmount {
  if (meta.tipo === 'mes') {
    return meta.importe;
  }
  const meses = Math.max(1, meta.meses);
  return Math.ceil(meta.importe / meses / 100) * 100;
}

/**
 * Fecha límite de una meta «Una vez»: el día 1 del mes que cae `meses`
 * después de `mes` (`YYYY-MM`). Octubre de 2026 + 12 → `2027-10-01`.
 */
export function fechaMeta(mes: string, meses: number): string {
  return `${monthUtils.addMonths(mes, meses)}-01`;
}

/** Nombre de un cofre en el idioma de la app (sin emoji). */
export function nombreCofre(t: TFunction, id: IdCofre | IdMeta): string {
  switch (id) {
    case 'vivienda':
      return t('Rent or mortgage');
    case 'suministros':
      return t('Electricity, water and gas');
    case 'internet':
      return t('Internet and phone');
    case 'piso':
      return t('Shared flat expenses');
    case 'super':
      return t('Supermarket');
    case 'transporte':
      return t('Transport');
    case 'restaurantes':
      return t('Restaurants');
    case 'ocio':
      return t('Leisure');
    case 'suscripciones':
      return t('Subscriptions');
    case 'videojuegos':
      return t('Video games');
    case 'cuidado':
      return t('Personal care');
    case 'mascota':
      return t('Pet');
    case 'hijos':
      return t('Children');
    case 'regalos':
      return t('Gifts and Christmas');
    case 'ropa':
      return t('Clothes');
    case 'vacaciones':
      return t('Holidays');
    case 'medico':
      return t('Doctor and dentist');
    case 'coche':
      return t('Car');
    case 'anuales':
      return t('Yearly payments');
    case 'colchon':
      return t('Emergency fund');
    case 'viaje':
      return t('A trip');
    case 'compra':
      return t('A big purchase');
    case 'carne':
      return t('Driving licence');
    case 'otra':
      return t('Another goal');
    default:
      return String(id);
  }
}

/** Nombre de la meta: el propio si es «Otra meta» y lo tiene. */
export function nombreMeta(t: TFunction, meta: MetaElegida): string {
  const propio = meta.nombre?.trim();
  if (meta.id === 'otra' && propio) {
    return propio;
  }
  return nombreCofre(t, meta.id);
}

/** Línea de ayuda de cada tarjeta de meta (paso 6). */
export function descripcionMeta(t: TFunction, id: IdMeta): string {
  switch (id) {
    case 'colchon':
      return t('For breakdowns, fines or a slow month');
    case 'viaje':
      return t('Flights, accommodation and spending there');
    case 'compra':
      return t('Phone, computer, motorbike…');
    case 'carne':
      return t('Driving school and fees');
    case 'otra':
      return t('You choose the name');
    default:
      return '';
  }
}

/** Nombre de cada grupo que crea la travesía. */
export function nombreGrupo(t: TFunction, grupo: GrupoTravesia): string {
  switch (grupo) {
    case 'casa':
      return t('Household');
    case 'dia':
      return t('Day to day');
    case 'vez':
      return t('Now and then');
    case 'obj':
      return t('Goals');
    default:
      return String(grupo);
  }
}
